import React, { useContext, useEffect, useState } from "react";
import { onAuthStateChange, initFromStorage, hasPendingStoredSession } from '../auth/jwtAuth';

export const UserContext = React.createContext();

/**
 * Provides the authenticated user session via React context.
 *
 * Session shape:
 *   {
 *     loading: boolean,
 *     user: { uid, email, displayName, role, roles, ... } | null,
 *     role: string | null,   // primary role (backward-compat for existing consumers)
 *     roles: string[] | null // all roles (multi-role)
 *   }
 *
 * Phase 1a: backed by JWT auth (POST /api/auth/login + GET /api/auth/me).
 * The user object exposes .uid which equals the PostgreSQL user_id.
 */
export const UserProvider = ({ children }) => {
    const [session, setSession] = useState({ loading: true, user: null, claims: null });

    useEffect(() => {
        // While a persisted refresh token is still being exchanged on a fresh
        // page load, treat the initial null notification as "still loading" so
        // the login page doesn't flash before the session is restored.
        let restoringFromStorage = hasPendingStoredSession();

        // Subscribe to JWT auth state changes
        const unsubscribe = onAuthStateChange((user) => {
            if (!user) {
                if (restoringFromStorage) {
                    // Keep showing the loading state until initFromStorage settles.
                    return;
                }
                setSession({ loading: false, user: null, role: null, roles: null });
            } else {
                const primaryRole = user.role || 'real_user';
                const allRoles = Array.isArray(user.roles) && user.roles.length > 0
                    ? user.roles
                    : [primaryRole];
                setSession({
                    loading: false,
                    user,
                    role: primaryRole,
                    roles: allRoles,
                    claims: { role: primaryRole, roles: allRoles },
                });
            }
        });

        // Restore session from localStorage on mount (survives page refreshes).
        initFromStorage()
            .catch(() => null)
            .then((restoredUser) => {
                restoringFromStorage = false;
                // If restoration produced no user, the listener was suppressed
                // above; emit the final unauthenticated state now.
                if (!restoredUser) {
                    setSession({ loading: false, user: null, role: null, roles: null });
                }
            });

        return () => unsubscribe();
    }, []);

    return (
        <UserContext.Provider value={session}>
            {!session.loading && children}
        </UserContext.Provider>
    );
};

export const useSession = () => useContext(UserContext);
